import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import type { DocumentRevision } from '@/entities/Document';
import {
  type CreateCombinedDocumentSendMetadataPayload,
  DocumentSendAudienceType,
  DocumentSendFormat,
  DocumentSendStatus,
  documentSendApi,
  getDocumentSendFieldErrors,
  isDocumentSendUserFacingError,
} from '@/entities/DocumentSend';
import { useAuthStore } from '@/entities/Session';
import { type Membership, useUserStore } from '@/entities/User';
import { MAX_DOCUMENT_SEND_ATTACHMENTS } from '@/shared/config';
import { useCustomToast } from '@/shared/lib/use';

import {
  type PhysicalRecipientOption,
  type PhysicalRecipientRow,
  type SelectedDocumentEntry,
} from './physicalRecipient';
import {
  buildLocalUrlsPayload,
  countLocalFiles,
  countLocalUrls,
  resolveRejectedUrlRows,
  selectLocalFiles,
  TOO_MANY_ATTACHMENTS_MESSAGE,
  URL_ROWS_REJECTED_MESSAGE,
  type ElectronicSendMode,
  type LocalUrl,
} from './electronic-attachments';
import {
  buildInvalidRecipientEmailsMessage,
  resolveInvalidRecipientEmailsMessage,
  validateElectronicRecipients,
} from './electronicRecipient';
import { useElectronicSendForm } from './useElectronicSendForm';
import {
  DOCUMENT_SEND_MAX_GD_DOCUMENTS,
  useGdDocumentSelection,
  type SelectedGdDocument,
} from './useGdDocumentSelection';

type SendFormatValue = DocumentSendFormat | '';
type SendAudienceValue = DocumentSendAudienceType | '';

interface PositionOption {
  id: string;
  name: string;
}

export { DOCUMENT_SEND_MAX_GD_DOCUMENTS };
export type { SelectedGdDocument };

export const MAX_SEND_TITLE_LENGTH = 255;

const DOCUMENT_SEND_MAX_TOTAL_ATTACHMENT_BYTES = 10_485_760;
const TOTAL_SIZE_EXCEEDED_MESSAGE =
  'O total de anexos ultrapassa 10MB. Remova arquivos para continuar.';
const INVALID_PHYSICAL_RECIPIENT_MESSAGE = 'Destinatário físico inválido.';

let combinedSendFallbackIdCounter = 0;

const createCombinedSendIdempotencyKey = (): string => {
  const cryptoApi = globalThis.crypto;

  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }

  if (cryptoApi?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  combinedSendFallbackIdCounter += 1;
  return `combined-send-${Date.now()}-${combinedSendFallbackIdCounter}`;
};

const toPositionOption = (
  id: number | undefined,
  name: string | undefined
): PositionOption | null => {
  if (id == null || !name) {
    return null;
  }

  return { id: String(id), name };
};

const createStartOfToday = (): Date => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const getMembershipPositions = (membership: Membership): PositionOption[] => {
  const primaryPosition = toPositionOption(membership.positionId, membership.positionName);
  const nestedPosition = toPositionOption(membership.position?.id, membership.position?.name);

  return [
    ...(primaryPosition ? [primaryPosition] : []),
    ...(nestedPosition ? [nestedPosition] : []),
    ...(membership.positions ?? []).map((position) => ({
      id: String(position.id),
      name: position.name,
    })),
  ];
};

const toISODateString = (date: Date): string => date.toISOString();

interface ActivePhysicalRecipient {
  recipient: PhysicalRecipientRow;
  options: PhysicalRecipientOption[];
  recipientType: 'CLIENT' | 'SUPPLIER';
}

interface CombinedPhysicalRecipientPayload {
  recipientType: 'CLIENT' | 'SUPPLIER';
  protheusCode: string;
  protheusStore: string;
  recipientNameSnapshot: string;
  copies: number;
  sendDate: string;
}

export function useDocumentSendCreateForm() {
  const userStore = useUserStore();
  const authStore = useAuthStore();
  const router = useRouter();
  const { showSuccessToast, showErrorToast, showWarningToast } = useCustomToast();

  const title = ref('');
  const selectedFormat = ref<SendFormatValue>('');
  const selectedAudienceType = ref<SendAudienceValue>('');
  const selectedElectronicMode = ref<ElectronicSendMode>('');
  const selectedPositionId = ref('');
  const sendDate = ref<Date | null>(createStartOfToday());
  const isDatePickerOpen = ref(false);
  const message = ref('');
  const combinedSendIdempotencyKey = ref<string | null>(null);
  const combinedSendFingerprint = ref<string | null>(null);
  const isDocumentSelectModalOpen = ref(false);
  const isSubmitting = ref(false);
  const selectedDocuments = ref<SelectedDocumentEntry[]>([]);
  const localFiles = ref<File[]>([]);
  const localUrls = ref<LocalUrl[]>([]);
  const urlErrors = ref<string[]>([]);
  const isLoadingElectronicRecipients = ref(false);

  const titleError = ref('');
  const messageError = ref('');
  const senderPositionError = ref('');
  const recipientError = ref('');
  const documentSelectionError = ref('');

  const collaboratorDisplayName = computed(() => userStore.name ?? 'Usuário atual');
  const filteredMemberships = computed(() => {
    const memberships = userStore.memberships ?? [];
    const selectedBranchId = authStore.selectedBranchId;

    if (selectedBranchId == null) {
      return memberships;
    }

    return memberships.filter((membership) => membership.branchId === selectedBranchId);
  });

  const positionOptions = computed<PositionOption[]>(() => {
    const seenIds = new Set<string>();

    return filteredMemberships.value.flatMap((membership) =>
      getMembershipPositions(membership).filter((position) => {
        if (seenIds.has(position.id)) {
          return false;
        }

        seenIds.add(position.id);
        return true;
      })
    );
  });

  const showsElectronicSection = computed(
    () =>
      selectedFormat.value === DocumentSendFormat.ELECTRONIC ||
      selectedFormat.value === DocumentSendFormat.ELECTRONIC_PHYSICAL
  );

  const showsPhysicalSection = computed(
    () =>
      selectedFormat.value === DocumentSendFormat.PHYSICAL ||
      selectedFormat.value === DocumentSendFormat.ELECTRONIC_PHYSICAL
  );

  const localAttachmentCount = computed(
    () =>
      countLocalFiles(selectedElectronicMode.value, localFiles.value) +
      countLocalUrls(selectedElectronicMode.value, localUrls.value)
  );

  const hasValidRecipients = computed(() => {
    if (selectedDocuments.value.length === 0) {
      return false;
    }

    return selectedDocuments.value.every(isPhysicalDocumentValid);
  });

  const canSubmit = computed(() => {
    if (!title.value.trim()) return false;
    if (!selectedPositionId.value) return false;
    if (selectedFormat.value !== DocumentSendFormat.PHYSICAL) return false;
    if (!selectedAudienceType.value) return false;
    if (sendDate.value === null) return false;
    return hasValidRecipients.value;
  });

  const {
    addSelectedGdDocument,
    removeSelectedGdDocument,
    replaceSelectedGdDocuments,
    selectedGdDocuments,
  } = useGdDocumentSelection({
    documentSelectionError,
    localFiles,
    localUrls,
    selectedElectronicMode,
    maxAttachmentCount: 10,
    maxAttachmentBytes: DOCUMENT_SEND_MAX_TOTAL_ATTACHMENT_BYTES,
  });

  const confirmGdDocumentSelection = (revisions: DocumentRevision[]): boolean => {
    const isConfirmed = replaceSelectedGdDocuments(revisions);

    if (!isConfirmed && documentSelectionError.value) {
      showErrorToast({
        title: 'Erro',
        message: documentSelectionError.value,
        type: 'error',
      });
    }

    return isConfirmed;
  };
  const gdDocumentRevisionIds = computed(() =>
    selectedFormat.value === DocumentSendFormat.ELECTRONIC_PHYSICAL
      ? selectedDocuments.value.map((document) => document.documentRevisionId)
      : selectedGdDocuments.value.map((document) => document.id)
  );
  const gdDocumentsTotalBytes = computed(() =>
    selectedFormat.value === DocumentSendFormat.ELECTRONIC_PHYSICAL
      ? selectedDocuments.value.reduce((total, document) => total + document.fileSizeBytes, 0)
      : selectedGdDocuments.value.reduce((total, document) => total + document.fileSizeBytes, 0)
  );
  const localFilesTotalBytes = computed(() =>
    localFiles.value.reduce((total, file) => total + file.size, 0)
  );
  const totalAttachmentBytes = computed(
    () => gdDocumentsTotalBytes.value + localFilesTotalBytes.value
  );

  const canSelectMoreDocuments = computed(() => {
    if (selectedFormat.value === DocumentSendFormat.PHYSICAL) {
      return true;
    }

    return (
      gdDocumentRevisionIds.value.length + localAttachmentCount.value <
      MAX_DOCUMENT_SEND_ATTACHMENTS
    );
  });

  const canSubmitElectronic = computed(
    () => selectedFormat.value === DocumentSendFormat.ELECTRONIC
  );

  const canSubmitCombined = computed(
    () =>
      selectedFormat.value === DocumentSendFormat.ELECTRONIC_PHYSICAL &&
      !!selectedAudienceType.value
  );

  const addDocuments = (entries: SelectedDocumentEntry[]) => {
    const existingIds = new Set(
      selectedDocuments.value.map((document) => document.documentRevisionId)
    );
    const newEntries = entries.filter((entry) => !existingIds.has(entry.documentRevisionId));
    const slotsAvailable =
      selectedFormat.value === DocumentSendFormat.PHYSICAL
        ? newEntries.length
        : MAX_DOCUMENT_SEND_ATTACHMENTS -
          selectedDocuments.value.length -
          localAttachmentCount.value;
    selectedDocuments.value = [...selectedDocuments.value, ...newEntries.slice(0, slotsAvailable)];
  };

  const removeDocument = (documentRevisionId: number) => {
    selectedDocuments.value = selectedDocuments.value.filter(
      (document) => document.documentRevisionId !== documentRevisionId
    );
  };

  const updateDocumentClientRecipients = (
    documentRevisionId: number,
    rows: PhysicalRecipientRow[]
  ) => {
    selectedDocuments.value = selectedDocuments.value.map((document) =>
      document.documentRevisionId === documentRevisionId
        ? { ...document, clientRecipients: rows }
        : document
    );
  };

  const updateDocumentSupplierRecipients = (
    documentRevisionId: number,
    rows: PhysicalRecipientRow[]
  ) => {
    selectedDocuments.value = selectedDocuments.value.map((document) =>
      document.documentRevisionId === documentRevisionId
        ? { ...document, supplierRecipients: rows }
        : document
    );
  };

  const getSenderMemberId = (): number | null => {
    const positionId = Number(selectedPositionId.value);
    const membership = filteredMemberships.value.find((item) => {
      if (item.positionId === positionId) return true;
      if (item.position?.id === positionId) return true;
      if (item.positions?.some((position) => position.id === positionId)) return true;
      return false;
    });

    return membership?.id ?? null;
  };

  const {
    clientOptions,
    electronicClientOptions,
    electronicClientRecipients,
    electronicSupplierOptions,
    electronicSupplierRecipients,
    setElectronicClientRecipients,
    setElectronicSupplierRecipients,
    setLocalFiles,
    setLocalUrls,
    setUrlErrors,
    submitElectronicSend,
    supplierOptions,
    validateElectronicUrls,
  } = useElectronicSendForm({
    gdDocumentRevisionIds,
    getSenderMemberId,
    isLoadingRecipientData: isLoadingElectronicRecipients,
    isSubmitting,
    localFiles,
    localUrls,
    urlErrors,
    message,
    messageError,
    recipientError,
    router,
    selectedAudienceType,
    selectedElectronicMode,
    selectedFormat,
    selectedPositionId,
    sendDate,
    senderPositionError,
    showErrorToast,
    showSuccessToast,
    showWarningToast,
    title,
    titleError,
  });

  const findPhysicalRecipient = (
    recipientId: string,
    options: PhysicalRecipientOption[]
  ): { protheusCode: string; protheusStore: string; recipientNameSnapshot: string } | null => {
    const option = options.find((item) => item.id === recipientId);
    const name = option?.name.trim();

    return option && name
      ? { protheusCode: option.code, protheusStore: option.store, recipientNameSnapshot: name }
      : null;
  };

  const getActivePhysicalRecipients = (
    document: SelectedDocumentEntry
  ): ActivePhysicalRecipient[] => {
    const activeRecipients: ActivePhysicalRecipient[] = [];

    if (
      selectedAudienceType.value === DocumentSendAudienceType.CLIENT ||
      selectedAudienceType.value === DocumentSendAudienceType.CLIENT_SUPPLIER
    ) {
      activeRecipients.push(
        ...document.clientRecipients.map((recipient) => ({
          recipient,
          options: clientOptions.value,
          recipientType: 'CLIENT' as const,
        }))
      );
    }

    if (
      selectedAudienceType.value === DocumentSendAudienceType.SUPPLIER ||
      selectedAudienceType.value === DocumentSendAudienceType.CLIENT_SUPPLIER
    ) {
      activeRecipients.push(
        ...document.supplierRecipients.map((recipient) => ({
          recipient,
          options: supplierOptions.value,
          recipientType: 'SUPPLIER' as const,
        }))
      );
    }

    return activeRecipients;
  };

  const isPhysicalRecipientValid = ({ recipient, options }: ActivePhysicalRecipient): boolean => {
    if (
      recipient.recipientId === '' ||
      recipient.sendDate === null ||
      recipient.copies === null ||
      recipient.copies < 1
    ) {
      return false;
    }

    return findPhysicalRecipient(recipient.recipientId, options) !== null;
  };

  const isPhysicalDocumentValid = (document: SelectedDocumentEntry): boolean => {
    const activeRecipients = getActivePhysicalRecipients(document);

    return activeRecipients.length > 0 && activeRecipients.every(isPhysicalRecipientValid);
  };

  const toCombinedPhysicalRecipientPayload = (
    recipient: PhysicalRecipientRow,
    recipientType: 'CLIENT' | 'SUPPLIER',
    options: PhysicalRecipientOption[]
  ): CombinedPhysicalRecipientPayload => {
    if (recipient.recipientId === '' || recipient.sendDate === null || recipient.copies === null) {
      throw new Error(INVALID_PHYSICAL_RECIPIENT_MESSAGE);
    }

    const physicalRecipient = findPhysicalRecipient(recipient.recipientId, options);

    if (!physicalRecipient) {
      throw new Error(INVALID_PHYSICAL_RECIPIENT_MESSAGE);
    }

    return {
      recipientType,
      ...physicalRecipient,
      copies: recipient.copies,
      sendDate: toISODateString(recipient.sendDate),
    };
  };

  const buildCombinedDocumentsPayload = () =>
    selectedDocuments.value.map((document) => ({
      documentRevisionId: document.documentRevisionId,
      physicalRecipients: [
        ...document.clientRecipients.map((recipient) =>
          toCombinedPhysicalRecipientPayload(recipient, 'CLIENT', clientOptions.value)
        ),
        ...document.supplierRecipients.map((recipient) =>
          toCombinedPhysicalRecipientPayload(recipient, 'SUPPLIER', supplierOptions.value)
        ),
      ],
    }));

  const showCombinedSendResultToast = (
    result: Awaited<ReturnType<typeof documentSendApi.createCombinedDocumentSend>>
  ) => {
    if (result.status === DocumentSendStatus.PARTIALLY_SENT) {
      const failedRecipients = result.failedEmailRecipients
        .map((recipient) => `${recipient.name} <${recipient.email}>`)
        .join(', ');

      showErrorToast({
        title: 'Parcialmente enviado',
        message: failedRecipients
          ? `O envio físico foi registrado, mas estes destinatários não receberam o e-mail: ${failedRecipients}`
          : 'O envio físico foi registrado, mas houve falha no envio dos e-mails.',
        type: 'error',
      });
      return;
    }

    if (result.status === DocumentSendStatus.EMAIL_DISPATCH_IN_PROGRESS) {
      showWarningToast({
        title: 'Envio em processamento',
        message: 'Este envio já foi iniciado. Acompanhe o resultado no controle de envios.',
        type: 'warning',
      });
      return;
    }

    showSuccessToast({
      title: 'Sucesso',
      message: 'Envio eletrônico e físico realizado com sucesso.',
      type: 'success',
    });
  };

  const resolveCombinedSendErrorMessage = (
    rejectedUrlRows: string[] | null,
    shouldSurfaceMessage: boolean,
    errorMessage: string,
    invalidRecipientEmailsMessage: string | null
  ) => {
    if (rejectedUrlRows !== null) {
      return URL_ROWS_REJECTED_MESSAGE;
    }

    if (invalidRecipientEmailsMessage !== null) {
      return invalidRecipientEmailsMessage;
    }

    if (shouldSurfaceMessage) {
      return errorMessage;
    }

    return 'Falha ao concluir envio, tente novamente';
  };

  const validatePhysicalForm = (): boolean => {
    titleError.value = title.value.trim() === '' ? 'Título é obrigatório.' : '';
    senderPositionError.value = selectedPositionId.value === '' ? 'Cargo é obrigatório.' : '';

    if (getSenderMemberId() === null) {
      senderPositionError.value = 'Selecione um cargo válido para o envio.';
    }

    return (
      titleError.value === '' &&
      senderPositionError.value === '' &&
      selectedDocuments.value.length > 0 &&
      selectedDocuments.value.every(isPhysicalDocumentValid)
    );
  };

  const validateCombinedForm = (): {
    isValid: boolean;
    recipients: Array<{
      type: 'CLIENT' | 'SUPPLIER';
      protheusCode: string;
      protheusStore: string;
      name: string;
      email: string;
    }>;
    invalidEmailCount: number;
  } => {
    titleError.value = title.value.trim() === '' ? 'Título é obrigatório.' : '';
    messageError.value = message.value.trim() === '' ? 'Mensagem é obrigatória.' : '';
    senderPositionError.value = selectedPositionId.value === '' ? 'Cargo é obrigatório.' : '';
    recipientError.value = '';

    const senderMemberId = getSenderMemberId();

    if (senderMemberId === null) {
      senderPositionError.value = 'Selecione um cargo válido para o envio.';
    }

    const {
      recipients,
      recipientError: electronicRecipientError,
      invalidEmailCount,
    } = validateElectronicRecipients({
      audienceType: selectedAudienceType.value,
      clientRecipients: electronicClientRecipients.value,
      supplierRecipients: electronicSupplierRecipients.value,
    });

    recipientError.value = electronicRecipientError;

    if (!validatePhysicalForm()) {
      recipientError.value = recipientError.value || 'Configure a cópia física dos documentos.';
    }

    if (totalAttachmentBytes.value > DOCUMENT_SEND_MAX_TOTAL_ATTACHMENT_BYTES) {
      documentSelectionError.value = TOTAL_SIZE_EXCEEDED_MESSAGE;
    }

    if (
      selectedDocuments.value.length + localAttachmentCount.value >
      MAX_DOCUMENT_SEND_ATTACHMENTS
    ) {
      documentSelectionError.value = documentSelectionError.value || TOO_MANY_ATTACHMENTS_MESSAGE;
    }

    urlErrors.value = validateElectronicUrls();

    return {
      isValid:
        titleError.value === '' &&
        messageError.value === '' &&
        senderPositionError.value === '' &&
        recipientError.value === '' &&
        documentSelectionError.value === '' &&
        urlErrors.value.every((urlError) => urlError === '') &&
        recipients.length > 0 &&
        hasValidRecipients.value,
      recipients,
      invalidEmailCount,
    };
  };

  const submitCombinedSend = async () => {
    if (selectedFormat.value !== DocumentSendFormat.ELECTRONIC_PHYSICAL || isSubmitting.value) {
      return;
    }

    const validation = validateCombinedForm();

    if (!validation.isValid) {
      if (validation.invalidEmailCount > 0) {
        showErrorToast({
          title: 'Erro',
          message: buildInvalidRecipientEmailsMessage(validation.invalidEmailCount),
          type: 'error',
        });
      }

      return;
    }

    const senderMemberId = getSenderMemberId();

    if (senderMemberId === null) {
      senderPositionError.value = 'Selecione um cargo válido para o envio.';
      return;
    }

    isSubmitting.value = true;

    try {
      const documents = buildCombinedDocumentsPayload();

      const metadataWithoutKey = {
        title: title.value.trim(),
        message: message.value.trim(),
        sendDate: sendDate.value?.toISOString() ?? new Date().toISOString(),
        senderMemberId,
        senderPositionId: Number(selectedPositionId.value),
        recipients: validation.recipients,
        documents,
        localUrls: buildLocalUrlsPayload(selectedElectronicMode.value, localUrls.value),
      };

      const metadataFingerprint = JSON.stringify(metadataWithoutKey);

      if (
        combinedSendIdempotencyKey.value === null ||
        combinedSendFingerprint.value !== metadataFingerprint
      ) {
        combinedSendIdempotencyKey.value = createCombinedSendIdempotencyKey();
        combinedSendFingerprint.value = metadataFingerprint;
      }

      const metadata: CreateCombinedDocumentSendMetadataPayload = {
        idempotencyKey: combinedSendIdempotencyKey.value,
        ...metadataWithoutKey,
      };

      const formData = new FormData();
      formData.append('metadata', JSON.stringify(metadata));
      selectLocalFiles(selectedElectronicMode.value, localFiles.value).forEach((file) =>
        formData.append('files', file)
      );

      const result = await documentSendApi.createCombinedDocumentSend(formData);

      showCombinedSendResultToast(result);

      await router.push('/gestao-documentos/controle-envio');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '';
      const shouldSurfaceMessage =
        isDocumentSendUserFacingError(error) || errorMessage === INVALID_PHYSICAL_RECIPIENT_MESSAGE;
      const fieldErrors = getDocumentSendFieldErrors(error);
      const rejectedUrlRows = resolveRejectedUrlRows(localUrls.value, fieldErrors);
      const invalidRecipientEmailsMessage = resolveInvalidRecipientEmailsMessage(fieldErrors);

      if (rejectedUrlRows !== null) {
        urlErrors.value = rejectedUrlRows;
      }

      if (invalidRecipientEmailsMessage !== null) {
        recipientError.value = invalidRecipientEmailsMessage;
      }

      const combinedSendErrorMessage = resolveCombinedSendErrorMessage(
        rejectedUrlRows,
        shouldSurfaceMessage,
        errorMessage,
        invalidRecipientEmailsMessage
      );

      showErrorToast({
        title: 'Erro',
        message: combinedSendErrorMessage,
        type: 'error',
      });
    } finally {
      isSubmitting.value = false;
    }
  };
  const handleSubmit = async () => {
    if (
      isSubmitting.value ||
      !validatePhysicalForm() ||
      !canSubmit.value ||
      sendDate.value === null
    ) {
      return;
    }

    isSubmitting.value = true;

    try {
      const documents = selectedDocuments.value.map((document) => {
        const physicalRecipients = getActivePhysicalRecipients(document).map(
          ({ recipient, options, recipientType }) => {
            if (
              recipient.recipientId === '' ||
              recipient.sendDate === null ||
              recipient.copies === null
            ) {
              throw new Error(INVALID_PHYSICAL_RECIPIENT_MESSAGE);
            }

            const physicalRecipient = findPhysicalRecipient(recipient.recipientId, options);

            if (!physicalRecipient) {
              throw new Error(INVALID_PHYSICAL_RECIPIENT_MESSAGE);
            }

            return {
              recipientType,
              ...physicalRecipient,
              copies: recipient.copies,
              sendDate: toISODateString(recipient.sendDate),
            };
          }
        );

        return {
          documentRevisionId: document.documentRevisionId,
          physicalRecipients,
        };
      });

      await documentSendApi.createDocumentSend({
        title: title.value.trim(),
        sendDate: toISODateString(sendDate.value),
        senderPositionId: Number(selectedPositionId.value),
        documents,
      });

      showSuccessToast({
        title: 'Sucesso',
        message: 'Envio físico registrado com sucesso.',
        type: 'success',
      });
      await router.push('/gestao-documentos/controle-envio');
    } catch (error) {
      showErrorToast({
        title: 'Erro',
        message: isDocumentSendUserFacingError(error)
          ? error.message
          : 'Falha ao concluir envio, tente novamente',
        type: 'error',
      });
      isSubmitting.value = false;
    }
  };

  const openSendDatePicker = () => {
    isDatePickerOpen.value = true;
  };

  const updateSendDate = (date: Date | null) => {
    sendDate.value = date;
  };

  watch(selectedFormat, (format) => {
    if (
      (format === DocumentSendFormat.PHYSICAL ||
        format === DocumentSendFormat.ELECTRONIC_PHYSICAL) &&
      !selectedAudienceType.value
    ) {
      selectedAudienceType.value = DocumentSendAudienceType.CLIENT;
    }
  });

  watch(selectedAudienceType, (audienceType) => {
    if (audienceType === DocumentSendAudienceType.CLIENT) {
      selectedDocuments.value = selectedDocuments.value.map((document) => ({
        ...document,
        supplierRecipients: [],
      }));
    }

    if (audienceType === DocumentSendAudienceType.SUPPLIER) {
      selectedDocuments.value = selectedDocuments.value.map((document) => ({
        ...document,
        clientRecipients: [],
      }));
    }
  });
  watch(title, () => {
    if (titleError.value && title.value.trim() !== '') {
      titleError.value = '';
    }
  });

  watch(message, () => {
    if (messageError.value && message.value.trim() !== '') {
      messageError.value = '';
    }
  });

  watch(selectedPositionId, () => {
    if (senderPositionError.value && selectedPositionId.value !== '') {
      senderPositionError.value = '';
    }
  });

  watch(totalAttachmentBytes, (value) => {
    if (
      documentSelectionError.value === TOTAL_SIZE_EXCEEDED_MESSAGE &&
      value <= DOCUMENT_SEND_MAX_TOTAL_ATTACHMENT_BYTES
    ) {
      documentSelectionError.value = '';
    }
  });

  watch([localAttachmentCount, () => selectedDocuments.value.length], () => {
    if (
      documentSelectionError.value === TOO_MANY_ATTACHMENTS_MESSAGE &&
      selectedDocuments.value.length + localAttachmentCount.value <= MAX_DOCUMENT_SEND_ATTACHMENTS
    ) {
      documentSelectionError.value = '';
    }
  });

  return {
    addDocuments,
    addSelectedGdDocument,
    canSelectMoreDocuments,
    canSubmitCombined,
    canSubmit,
    canSubmitElectronic,
    collaboratorDisplayName,
    clientOptions,
    confirmGdDocumentSelection,
    documentSelectionError,
    electronicClientOptions,
    electronicClientRecipients,
    electronicSupplierOptions,
    electronicSupplierRecipients,
    gdDocumentRevisionIds,
    gdDocumentsTotalBytes,
    handleSubmit,
    isDatePickerOpen,
    isDocumentSelectModalOpen,
    isLoadingElectronicRecipients,
    isSubmitting,
    localAttachmentCount,
    localFiles,
    localUrls,
    urlErrors,
    message,
    messageError,
    openSendDatePicker,
    positionOptions,
    recipientError,
    removeDocument,
    removeSelectedGdDocument,
    selectedAudienceType,
    selectedDocuments,
    selectedElectronicMode,
    selectedFormat,
    selectedGdDocuments,
    selectedPositionId,
    sendDate,
    senderPositionError,
    setElectronicClientRecipients,
    setElectronicSupplierRecipients,
    setLocalFiles,
    setLocalUrls,
    setUrlErrors,
    showsElectronicSection,
    showsPhysicalSection,
    submitCombinedSend,
    submitElectronicSend,
    supplierOptions,
    title,
    titleError,
    updateDocumentClientRecipients,
    updateDocumentSupplierRecipients,
    updateSendDate,
  };
}
